import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Lock, Save, Loader2, Eye, EyeOff, Shield, Mail, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout, { useAdminAura } from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import api, { aurasApi } from '../../lib/api';

// Admin Avatar with Aura effect
const AdminAvatar = ({ name, auraPreset, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-16 h-16 text-2xl',
    lg: 'w-24 h-24 text-4xl'
  };

  const getAuraStyle = () => {
    if (!auraPreset) return {};
    
    // Parse aura colors
    const auraStyles = {
      'golden': { background: 'linear-gradient(135deg, #D4AF37, #F4D03F, #D4AF37)', boxShadow: '0 0 20px rgba(212, 175, 55, 0.5)' },
      'silver': { background: 'linear-gradient(135deg, #C0C0C0, #E8E8E8, #C0C0C0)', boxShadow: '0 0 20px rgba(192, 192, 192, 0.5)' },
      'ruby': { background: 'linear-gradient(135deg, #E31C3D, #FF6B6B, #E31C3D)', boxShadow: '0 0 20px rgba(227, 28, 61, 0.5)' },
      'emerald': { background: 'linear-gradient(135deg, #2ECC71, #58D68D, #2ECC71)', boxShadow: '0 0 20px rgba(46, 204, 113, 0.5)' },
      'sapphire': { background: 'linear-gradient(135deg, #3498DB, #5DADE2, #3498DB)', boxShadow: '0 0 20px rgba(52, 152, 219, 0.5)' },
      'amethyst': { background: 'linear-gradient(135deg, #9B59B6, #BB8FCE, #9B59B6)', boxShadow: '0 0 20px rgba(155, 89, 182, 0.5)' },
      'obsidian': { background: 'linear-gradient(135deg, #1C1C1C, #434343, #1C1C1C)', boxShadow: '0 0 20px rgba(67, 67, 67, 0.5)' },
      'sunset': { background: 'linear-gradient(135deg, #FF6B35, #F7931E, #FF6B35)', boxShadow: '0 0 20px rgba(255, 107, 53, 0.5)' },
    };

    return auraStyles[auraPreset] || {};
  };

  const auraStyle = getAuraStyle();
  const hasAura = auraPreset && Object.keys(auraStyle).length > 0;

  return (
    <div className="relative">
      {hasAura && (
        <div 
          className={`absolute inset-[-4px] rounded-full animate-pulse`}
          style={auraStyle}
        />
      )}
      <div 
        className={`${sizeClasses[size]} rounded-full bg-zinc-800 flex items-center justify-center font-black text-[#D4AF37] relative z-10 border-2 border-zinc-700`}
      >
        {name?.charAt(0)?.toUpperCase() || '?'}
      </div>
    </div>
  );
};

// Aura preview component
const AuraPreview = ({ preset, selected, onClick }) => {
  const auraStyles = {
    'golden': { colors: ['#D4AF37', '#F4D03F'], name: 'Dorado' },
    'silver': { colors: ['#C0C0C0', '#E8E8E8'], name: 'Plata' },
    'ruby': { colors: ['#E31C3D', '#FF6B6B'], name: 'Rubí' },
    'emerald': { colors: ['#2ECC71', '#58D68D'], name: 'Esmeralda' },
    'sapphire': { colors: ['#3498DB', '#5DADE2'], name: 'Zafiro' },
    'amethyst': { colors: ['#9B59B6', '#BB8FCE'], name: 'Amatista' },
    'obsidian': { colors: ['#1C1C1C', '#434343'], name: 'Obsidiana' },
    'sunset': { colors: ['#FF6B35', '#F7931E'], name: 'Atardecer' },
  };

  const style = auraStyles[preset];
  if (!style) return null;

  return (
    <button
      onClick={() => onClick(preset)}
      className={`relative p-4 rounded-lg border-2 transition-all ${
        selected 
          ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
          : 'border-zinc-700 hover:border-zinc-500 bg-zinc-900'
      }`}
    >
      {selected && (
        <div className="absolute top-2 right-2">
          <Check size={16} className="text-[#D4AF37]" />
        </div>
      )}
      <div 
        className="w-12 h-12 rounded-full mx-auto mb-2"
        style={{
          background: `linear-gradient(135deg, ${style.colors[0]}, ${style.colors[1]})`,
          boxShadow: `0 0 15px ${style.colors[0]}40`
        }}
      />
      <p className="text-xs text-center text-zinc-400">{style.name}</p>
    </button>
  );
};

const MyProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const { auraPreset: globalAura, updateAura } = useAdminAura();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingAura, setSavingAura] = useState(false);
  const [selectedAura, setSelectedAura] = useState(null);
  
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const auraPresets = ['golden', 'silver', 'ruby', 'emerald', 'sapphire', 'amethyst', 'obsidian', 'sunset'];

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || ''
      });
      setSelectedAura(user.aura_preset || globalAura || null);
    }
  }, [user, globalAura]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!profileData.full_name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setSavingProfile(true);
    try {
      await api.put('/auth/me', {
        full_name: profileData.full_name.trim(),
        email: profileData.email.trim() || null
      });
      
      toast.success('Perfil actualizado');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAuraChange = async (preset) => {
    setSelectedAura(preset);
    setSavingAura(true);
    try {
      await api.put('/auth/me', { aura_preset: preset });
      updateAura(preset); // Update global admin aura
      toast.success('Aura actualizado');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Error al actualizar aura');
    } finally {
      setSavingAura(false);
    }
  };

  const handleClearAura = async () => {
    setSelectedAura(null);
    setSavingAura(true);
    try {
      await api.put('/auth/me', { aura_preset: null });
      updateAura(null); // Clear global admin aura
      toast.success('Aura removido');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Error al remover aura');
    } finally {
      setSavingAura(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordData.old_password || !passwordData.new_password) {
      toast.error('Completá todos los campos');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSavingPassword(true);
    try {
      await api.post('/auth/change-password-json', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Contraseña actualizada');
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
              <User className="text-[#D4AF37]" />
              Mi Perfil
            </h1>
            <p className="text-zinc-500 mt-2">
              Administrá tu información personal, seguridad y apariencia
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Profile & Password */}
            <div className="lg:col-span-2 space-y-8">
              {/* User Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-zinc-900 border border-zinc-800 p-6"
              >
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-zinc-800">
                  <AdminAvatar 
                    name={user?.full_name} 
                    auraPreset={selectedAura}
                    size="md"
                  />
                  <div>
                    <h2 className="text-xl font-bold">{user?.full_name}</h2>
                    <p className="text-zinc-500 text-sm">@{user?.username}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Shield size={12} style={{ color: user?.role_color || '#D4AF37' }} />
                      <span 
                        className="text-xs font-bold uppercase"
                        style={{ color: user?.role_color || '#D4AF37' }}
                      >
                        {user?.role_name || 'Admin'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Profile Form */}
                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-4">
                    Información Personal
                  </h3>
                  
                  <div>
                    <Label>Nombre Completo</Label>
                    <Input
                      value={profileData.full_name}
                      onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  
                  <div>
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 pl-10"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={savingProfile}
                    className="bg-[#D4AF37] hover:bg-[#B8963C] text-black"
                  >
                    {savingProfile ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <Save size={16} className="mr-2" />
                    )}
                    Guardar Cambios
                  </Button>
                </form>
              </motion.div>

              {/* Password Change */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-zinc-900 border border-zinc-800 p-6"
              >
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Lock size={16} />
                  Cambiar Contraseña
                </h3>
                
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <Label>Contraseña Actual</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.old ? "text" : "password"}
                        value={passwordData.old_password}
                        onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, old: !showPasswords.old})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        {showPasswords.old ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Nueva Contraseña</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Confirmar Contraseña</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      >
                        {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={savingPassword}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    {savingPassword ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : (
                      <Lock size={16} className="mr-2" />
                    )}
                    Cambiar Contraseña
                  </Button>
                </form>
              </motion.div>
            </div>

            {/* Right Column - Aura Selector */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900 border border-zinc-800 p-6"
            >
              <h3 className="text-sm font-bold uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2">
                <Sparkles size={16} />
                Mi Aura
              </h3>
              
              <p className="text-zinc-500 text-sm mb-6">
                Elegí un aura para personalizar tu avatar
              </p>

              {/* Current Aura Preview */}
              <div className="flex justify-center mb-6">
                <AdminAvatar 
                  name={user?.full_name} 
                  auraPreset={selectedAura}
                  size="lg"
                />
              </div>

              {savingAura && (
                <div className="flex justify-center mb-4">
                  <Loader2 className="animate-spin text-[#D4AF37]" size={20} />
                </div>
              )}

              {/* Aura Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {auraPresets.map((preset) => (
                  <AuraPreview
                    key={preset}
                    preset={preset}
                    selected={selectedAura === preset}
                    onClick={handleAuraChange}
                  />
                ))}
              </div>

              {/* Clear Aura */}
              {selectedAura && (
                <Button
                  variant="outline"
                  onClick={handleClearAura}
                  className="w-full border-zinc-700 text-zinc-400"
                  disabled={savingAura}
                >
                  Quitar Aura
                </Button>
              )}
            </motion.div>
          </div>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default MyProfilePage;
